import { Link } from "react-router-dom";
import NeutralHeader from "@/components/NeutralHeader";
import MinimalFooter from "@/components/MinimalFooter";
import { useSeo } from "@/lib/seo";
import { Users, Settings, FileText, MessageCircle, ArrowRight, Phone, Mail } from "lucide-react";

const TILES = [
  {
    icon: Users,
    title: "Inclusive Design",
    copy: "We design with all users in mind, including older adults and people with disabilities, to help ensure a positive experience for everyone.",
  },
  {
    icon: Settings,
    title: "Ongoing Improvements",
    copy: "We regularly review our website and make updates to improve accessibility, usability, and overall user experience.",
  },
  {
    icon: FileText,
    title: "WCAG 2.2 AA",
    copy: "We strive to follow WCAG 2.2 Level AA-oriented practices to help make our content more perceivable, operable, understandable, and robust.",
  },
  {
    icon: MessageCircle,
    title: "Your Feedback Matters",
    copy: "We welcome your feedback on accessibility. If you experience any difficulty using our website, please let us know so we can work to address it.",
  },
];

export default function Accessibility() {
  useSeo({
    title: "Accessibility | Financial Future Readiness Assessment",
    description:
      "Our commitment to accessibility. We follow WCAG 2.2 AA-oriented practices and continually work to improve the usability of this website for everyone, including older adults and people with disabilities.",
    canonicalPath: "/accessibility",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <NeutralHeader />
      <main id="main-content" tabIndex={-1} className="flex-1" data-testid="accessibility-main">

        {/* HERO — full-width atmospheric mountain scrim */}
        <section className="atmo-mountain-soft">
          <div className="summit-container py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="section-pill mb-5" data-testid="a11y-eyebrow">Accessibility</div>
              <h1
                className="font-serif"
                style={{ color: "#1E1968", fontSize: "clamp(2.4rem, 5vw, 4rem)", fontWeight: 600, lineHeight: 1.04, letterSpacing: "-0.015em" }}
                data-testid="a11y-hero-title"
              >
                Our Commitment<br />to Accessibility
              </h1>
              <p className="mt-6 text-lg" style={{ color: "#17233D", maxWidth: 580, lineHeight: 1.55 }} data-testid="a11y-hero-sub">
                We are committed to providing a website that is accessible to the widest possible audience, including older adults
                and people with disabilities.
              </p>
              <p className="mt-4 text-base" style={{ color: "#3a4d70", maxWidth: 580, lineHeight: 1.65 }}>
                We follow Web Content Accessibility Guidelines (WCAG) 2.2 AA-oriented practices and continually work to improve the
                accessibility and usability of our website so everyone can access the information and tools they need.
              </p>
            </div>
          </div>
        </section>

        {/* FOUR TILES ROW */}
        <section style={{ background: "#ffffff" }}>
          <div className="summit-container py-14 lg:py-16">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="a11y-tiles">
              {TILES.map(({ icon: Icon, title, copy }) => (
                <div
                  key={title}
                  className="summit-card"
                  data-testid={`a11y-tile-${title.toLowerCase().replace(/\s+/g, "-")}`}
                  style={{ background: "#ffffff" }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      width: 56, height: 56,
                      background: "#eaf2fa", color: "#1E1968",
                      borderRadius: 999,
                      border: "1px solid #cfe0f1",
                      marginBottom: 18,
                    }}
                  >
                    <Icon style={{ width: 26, height: 26 }} strokeWidth={1.5} />
                  </span>
                  <h2 className="font-serif" style={{ color: "#1E1968", fontSize: "1.2rem", fontWeight: 600, lineHeight: 1.25 }}>
                    {title}
                  </h2>
                  <p className="mt-3 text-base" style={{ color: "#3a4d70", lineHeight: 1.65 }}>{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OUR APPROACH — long-form text block */}
        <section style={{ background: "#F5F8FC", borderTop: "1px solid #e6edf5" }}>
          <div className="summit-container py-14 lg:py-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <h2
                className="font-serif"
                style={{ color: "#1E1968", fontSize: "clamp(1.6rem, 2.4vw, 2rem)", fontWeight: 600, lineHeight: 1.2 }}
                data-testid="a11y-approach-heading"
              >
                Our Approach
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-4 text-base" style={{ color: "#17233D", lineHeight: 1.7, maxWidth: 680 }}>
              <p>
                We work to improve the accessibility and usability of this website on an ongoing basis. In doing so, we aim to follow many
                of the recommendations of the World Wide Web Consortium (W3C) Web Content Accessibility Guidelines (WCAG) 2.2 at Level AA.
                These guidelines explain how to make web content more accessible to people with a wide range of abilities.
              </p>
              <p style={{ color: "#3a4d70" }}>
                We regularly review the site's content, design, and technology to help ensure that it remains easy to use, understandable, and
                compatible with assistive technologies. Accessibility is an ongoing effort, and some parts of the site may not yet fully meet
                every guideline — we welcome feedback so we can continue to improve.
              </p>
            </div>
          </div>
        </section>

        {/* WE'RE HERE TO HELP — no stock photography */}
        <section style={{ background: "#eaf2fa" }} aria-labelledby="a11y-help-heading">
          <div className="summit-container py-14 lg:py-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <h2
                id="a11y-help-heading"
                className="font-serif"
                style={{ color: "#1E1968", fontSize: "clamp(1.75rem, 2.6vw, 2.25rem)", fontWeight: 600, lineHeight: 1.2 }}
                data-testid="a11y-help-heading"
              >
                We&rsquo;re Here to Help
              </h2>
              <p className="mt-4 text-base" style={{ color: "#17233D", lineHeight: 1.7, maxWidth: 580 }}>
                If you experience any difficulty using any part of this website, or if you have a suggestion on how we can improve,
                please contact us using the details below. We will work with you to provide the information, service, or assistance
                you need through a reasonable communication method.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                <a
                  href="mailto:info@summit-rs.com"
                  className="btn-primary"
                  data-testid="a11y-contact-cta"
                  aria-label="Email Summit Retirement Solutions at info@summit-rs.com"
                >
                  Contact Us <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
                </a>
                <a
                  href="tel:+18608547099"
                  className="inline-flex items-center gap-2 text-base"
                  style={{ color: "#17233D", textDecoration: "none" }}
                  data-testid="a11y-phone"
                >
                  <Phone className="w-4 h-4" style={{ color: "#258FC4" }} aria-hidden="true" />
                  <span style={{ fontWeight: 500 }}>860-854-7099</span>
                </a>
                <a
                  href="mailto:info@summit-rs.com"
                  className="inline-flex items-center gap-2 text-base"
                  style={{ color: "#17233D", textDecoration: "none" }}
                  data-testid="a11y-email"
                >
                  <Mail className="w-4 h-4" style={{ color: "#258FC4" }} aria-hidden="true" />
                  <span style={{ fontWeight: 500 }}>info@summit-rs.com</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5" style={{ borderLeft: "1px solid #cfe0f1", paddingLeft: 32 }}>
              <div className="font-serif" style={{ color: "#1E1968", fontSize: "1.15rem", fontWeight: 600, lineHeight: 1.3 }}>
                Financial Future Readiness Assessment
              </div>
              <p className="mt-3 text-base" style={{ color: "#17233D", lineHeight: 1.65 }}>
                If you&rsquo;d like, you can also start the educational{" "}
                <Link to="/assessment" className="underline" style={{ color: "#1E1968", fontWeight: 500 }}>
                  Financial Future Readiness Assessment
                </Link>
                . It is designed to be completed with keyboard navigation and standard assistive technologies.
              </p>
            </div>
          </div>
        </section>

      </main>
      <MinimalFooter />
    </div>
  );
}
